/* Adds one-decimal scores to review summaries while individual reviews keep stars only. */
(() => {
  if (window.__buddyOkendoStarRatingLoaded) return;
  window.__buddyOkendoStarRatingLoaded = true;

  const widgetSelector = '[data-oke-star-rating]';
  // Minimal badges summarize multiple reviews; review cards and rating filters do not.
  const summaryStarsSelector = '.okeReviews .oke-minimalBadge-stars .oke-stars';
  const aggregateRatingSelector = '.oke-w-ratingAverageModule-rating-average';
  const countSelector = [
    '[data-oke-star-rating] .oke-sr-count',
    '.oke-minimalBadge-count',
    '.oke-w-ratingAverageModule-count',
  ].join(',');

  const markClickableReviewSummary = (element) => {
    const clickable = element?.matches('a[href], button, [role="button"]')
      ? element
      : element?.closest('a[href], button, [role="button"]');

    clickable?.classList.add('buddy-review-link');
  };

  const linkLandingPageWidget = (widget, row) => {
    const productSection = widget.closest('[data-buddy-product-reviews-url]');
    const reviewsUrl = productSection?.dataset.buddyProductReviewsUrl;
    if (!reviewsUrl || row.closest('a')) return;

    const ratingLabel = row.querySelector('.oke-sr-stars .oke-a11yText')?.textContent.trim();
    const reviewCount = row.querySelector('.oke-sr-count')?.textContent.trim();
    const link = document.createElement('a');
    link.className = 'buddy-okendo-summary-link buddy-review-link';
    link.href = reviewsUrl;
    if (ratingLabel && reviewCount) {
      link.setAttribute('aria-label', `${ratingLabel}. Read ${reviewCount}.`);
    }
    row.before(link);
    link.append(row);
  };

  const enhanceWidget = (widget) => {
    const rating = widget.querySelector('.oke-sr-rating');
    if (!rating) return;

    const row = rating.parentElement;
    row?.classList.add('buddy-okendo-summary-row');
    markClickableReviewSummary(row);
    if (row) linkLandingPageWidget(widget, row);

    // Okendo's visually hidden star label already announces the same score.
    rating.setAttribute('aria-hidden', 'true');
  };

  const parseRating = (stars) => {
    const label = stars.querySelector('.oke-a11yText')?.textContent || '';
    const match = label.match(/\d+(?:[.,]\d+)?/);
    if (!match) return null;

    const rating = Number(match[0].replace(',', '.'));
    if (!Number.isFinite(rating) || rating < 1 || rating > 5) return null;

    return rating.toFixed(1).replace('.', match[0].includes(',') ? ',' : '.');
  };

  const enhanceReviewCount = (count) => {
    const label = count.textContent || count.getAttribute('aria-label') || '';
    const match = label.match(/([\d,.]+)\s+reviews?/i);
    if (!match) return;

    const value = match[1];
    const numericValue = Number(value.replace(/,/g, ''));
    const reviewLabel = numericValue === 1 ? 'review' : 'reviews';

    count.classList.add('buddy-okendo-review-count');
    count.setAttribute('data-buddy-review-count', `${value} ${reviewLabel}`);
    count.setAttribute('aria-label', `${value} ${reviewLabel}`);
    markClickableReviewSummary(count);
  };

  const hideDuplicateAggregateRating = (rating) => {
    // The adjacent Okendo star label already announces the complete rating.
    rating.setAttribute('aria-hidden', 'true');
  };

  const enhanceSummaryStars = (stars) => {
    const rating = parseRating(stars);
    if (!rating) return;

    let number = stars.parentElement?.querySelector(':scope > .buddy-okendo-rating-number');
    if (!number) {
      number = document.createElement('span');
      number.className = 'buddy-okendo-rating-number';
      number.setAttribute('aria-hidden', 'true');
    }
    if (number.textContent !== rating) number.textContent = rating;
    if (stars.nextElementSibling !== number) stars.after(number);
    markClickableReviewSummary(stars);
  };

  const initialize = (root = document) => {
    if (root instanceof Element && root.matches(widgetSelector)) {
      enhanceWidget(root);
    }
    root.querySelectorAll?.(widgetSelector).forEach(enhanceWidget);

    if (root instanceof Element && root.matches(summaryStarsSelector)) {
      enhanceSummaryStars(root);
    }
    root.querySelectorAll?.(summaryStarsSelector).forEach(enhanceSummaryStars);

    if (root instanceof Element && root.matches(countSelector)) {
      enhanceReviewCount(root);
    }
    root.querySelectorAll?.(countSelector).forEach(enhanceReviewCount);

    if (root instanceof Element && root.matches(aggregateRatingSelector)) {
      hideDuplicateAggregateRating(root);
    }
    root.querySelectorAll?.(aggregateRatingSelector).forEach(hideDuplicateAggregateRating);
  };

  const pageObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const target = mutation.target instanceof Element
        ? mutation.target
        : mutation.target.parentElement;
      const widget = target?.closest(widgetSelector);
      if (widget) enhanceWidget(widget);

      const stars = target?.closest(summaryStarsSelector);
      if (stars) enhanceSummaryStars(stars);

      const count = target?.closest(countSelector);
      if (count) enhanceReviewCount(count);

      const aggregateRating = target?.closest(aggregateRatingSelector);
      if (aggregateRating) hideDuplicateAggregateRating(aggregateRating);

      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        initialize(node);
        const addedWidget = node.closest(widgetSelector);
        if (addedWidget) enhanceWidget(addedWidget);
      });
    });
  });

  const start = () => {
    initialize();
    pageObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
